import gql from 'not-graphql-tag';
import {
  AccountDetailList,
  AccountDetailListParams,
  AccountStatusType,
  AccountType,
  BusinessNeedsCategory,
  EnergyConsultationMonthAvailabilityResponse,
  EnergyConsultationRequest,
  EnergyConsultationResponse,
  MutationSubmitEnergyConsultationArgs,
  MutationSubmitEnergyConsultationWatArgs,
  PreferredContactMethod,
  PreferredLanguage,
  QueryGetEnergyConsultationMonthAvailabilityArgs,
  WatEnergyConsultationRequest,
} from '../__generated__/pge-types';
import { useMutation } from '@apollo/react-hooks';
import useAccountCustomer from './useAccountCustomer';
import useFormState from './useFormState';
import { createEnergyConsultationFormValidateFunction } from '../components/energy-consultation/NewConsultationForm/form.rules';
import { EnergyConsultationFormModel } from '../components/energy-consultation/types';
import { parsePhoneNumber } from '../util/format';
import { useContext, useEffect, useState } from 'react';
import {
  getAvailableDatesForConsultation,
  getCommercialAccountsList,
  submitEnergyConsultationMutation,
  submitEnergyConsultationWatMutation,
} from '../components/energy-consultation/queries';
import useAuthQuery from './useAuthQuery';
import { VirtualAccountType } from '../constants/customEnum';
import useWrapWithLoader from './useWrapWithLoading';
import { NotificationsContext } from '../providers/NotificationsProvider';
import { useTranslation } from './useTranslation';
import format from 'date-fns/format';
import { ApolloError } from 'apollo-client';

const EnergyConsultationFormDefaultValues: EnergyConsultationFormModel = {
  accountNumber: '',
  isRequestorNameFieldsVisible: false,
  serviceAddress: undefined,
  businessName: undefined,
  numberOfEmployees: undefined,
  state: 'OR',
  preferredContactMethod: PreferredContactMethod.Email,
  preferredLanguage: PreferredLanguage.English,
  selectedNeeds: [],
  commercialRetailOfficeOptions: [],
  largeCommercialIndustrialOptions: [],
};

export const useEnergyConsultationForm = ({
  onSuccess,
  isPublicForm = false,
}: {
  onSuccess?: () => void;
  isPublicForm?: boolean;
}) => {
  const { customer, loading: loadingCustomer } = useAccountCustomer();
  const { wrapWithLoader } = useWrapWithLoader();
  const notificationContext = useContext(NotificationsContext);
  const { t } = useTranslation();
  const [formKey, setFormKey] = useState(0);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [publicFormErrorMessage, setPublicFormErrorMessage] = useState('');

  const contactDetail = customer?.contactDetails;
  const pnpPhoneData =
    contactDetail && contactDetail.find(pnp => pnp?.contactType === 'PNP');
  const pnpPhone = pnpPhoneData?.contactValue
    ? parsePhoneNumber(pnpPhoneData?.contactValue)
    : '';
  const mobNumberData =
    contactDetail && contactDetail.find(mob => mob?.contactType === 'MOB');
  const mobNumber = mobNumberData?.contactValue
    ? parsePhoneNumber(mobNumberData?.contactValue)
    : '';
  const altNumberData =
    contactDetail &&
    contactDetail.find(altMob => altMob?.contactType === 'ALT');
  const altNumber = altNumberData?.contactValue
    ? parsePhoneNumber(altNumberData?.contactValue)
    : '';

  const form = useFormState(
    {
      ...EnergyConsultationFormDefaultValues,
    },
    {
      validate: createEnergyConsultationFormValidateFunction(),
      validationContext: { isPublicForm },
    },
  );

  const [createEnergyConsultationRequest] = useMutation<
    {
      submitEnergyConsultation: EnergyConsultationResponse;
    },
    MutationSubmitEnergyConsultationArgs
  >(submitEnergyConsultationMutation);

  const [createPublicEnergyConsultationRequest] = useMutation<
    {
      submitEnergyConsultationWat: EnergyConsultationResponse;
    },
    MutationSubmitEnergyConsultationWatArgs
  >(submitEnergyConsultationWatMutation);

  useEffect(() => {
    void form.setValue('accountHolderName', customer?.personName || '');
    void form.setValue('emailAddress', customer?.email || '');
    void form.setValue(
      'phoneNumber',
      pnpPhone ? pnpPhone : mobNumber ? mobNumber : altNumber ? altNumber : '',
    );
    void form.setValue('preferredLanguage', customer?.prefLanguage || '');
  }, [customer, formKey]);

  const { error, data: accountsData, loading: loadingAccounts } = useAuthQuery<
    { getAccountDetailList: AccountDetailList },
    { params: AccountDetailListParams }
  >(getCommercialAccountsList, {
    variables: {
      params: {
        groupId: VirtualAccountType.ALL_ACCTS,
        accountTypeFilter: [AccountType.Com],
        paging: undefined,
        accountStatus: AccountStatusType.Active,
      },
    },
    fetchPolicy: 'network-only',
  });
  const preferredLanguage = form.values.preferredLanguage;
  const postalCode = form.values.postalCode;

  const { data: availableDaysData } = useAuthQuery<
    {
      getEnergyConsultationMonthAvailability: EnergyConsultationMonthAvailabilityResponse;
    },
    QueryGetEnergyConsultationMonthAvailabilityArgs
  >(getAvailableDatesForConsultation, {
    variables: {
      input: {
        preferredLanguage,
        postalCode,
      },
    },
    skip: !preferredLanguage || !postalCode,
  });

  const setNotification = (
    message: string,
    severity: 'error' | 'success' = 'success',
  ) => {
    notificationContext?.setState?.({
      isOpen: true,
      severity,
      message,
    });

    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const onSubmit = wrapWithLoader(async () => {
    if (!customer?.encryptedPersonId) {
      setNotification(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'), 'error');
      return;
    }

    const payload: EnergyConsultationRequest = {
      encryptedPersonId: customer?.encryptedPersonId,
      accountNumber: form.values.accountNumber!,
      businessName: form.values.businessName,
      numberOfEmployees: form.values.numberOfEmployees
        ? parseInt(form.values.numberOfEmployees)
        : undefined,
      accountHolderName: form.values.accountHolderName!,
      ...(form.values.isRequestorNameFieldsVisible
        ? {
            requestorFirstName: form.values.requestorFirstName!,
            requestorLastName: form.values.requestorLastName!,
          }
        : {}),
      jobTitle: form.values.jobTitle,
      emailAddress: form.values.emailAddress!,
      phoneNumber: form.values.phoneNumber!,
      preferredContactMethod: form.values.preferredContactMethod!,
      preferredLanguage: form.values.preferredLanguage,
      consultationType: form.values.consultationType!,
      preferredDate: form.values.scheduleDate
        ? format(form.values.scheduleDate, 'yyyy-MM-dd')
        : undefined,
      preferredTimeSlot: form.values.scheduleTime!,
      businessNeedsCategory: form.values.businessNeedsCategory!,
      commercialRetailOfficeOptions:
        form.values.businessNeedsCategory ===
        BusinessNeedsCategory.CommercialRetailOffice
          ? form.values.commercialRetailOfficeOptions
          : undefined,
      largeCommercialIndustrialOptions:
        form.values.businessNeedsCategory ===
        BusinessNeedsCategory.LargeCommercialIndustrial
          ? form.values.largeCommercialIndustrialOptions
          : undefined,
      otherBusinessNeed: form.values.otherBusinessNeed,
      comments: form.values.comments,
    };

    try {
      setIsFormSubmitting(true);
      const { data } = await createEnergyConsultationRequest({
        variables: {
          payload,
        },
        refetchQueries: ['getEnergyConsultationRequests'],
      });
      if (data?.submitEnergyConsultation?.success) {
        setNotification(t('ENERGY_CONSULTATON_REQUEST_CREATED_SUCCESSFULLY'));
        form.reset({ ...EnergyConsultationFormDefaultValues });
        setFormKey(prev => prev + 1);
        onSuccess?.();
      } else {
        setNotification(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'), 'error');
      }
    } catch (e) {
      setNotification(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'), 'error');
    } finally {
      setIsFormSubmitting(false);
    }
  });

  const onSubmitPublicForm = wrapWithLoader(async () => {
    const payload: WatEnergyConsultationRequest = {
      accountId: form.values.accountNumber!,
      businessName: form.values.businessName,
      numberOfEmployees: form.values.numberOfEmployees
        ? parseInt(form.values.numberOfEmployees)
        : undefined,
      accountHolderName: form.values.accountHolderName!,
      jobTitle: form.values.jobTitle,
      emailAddress: form.values.emailAddress!,
      phoneNumber: form.values.phoneNumber!,
      preferredContactMethod: form.values.preferredContactMethod!,
      preferredLanguage: form.values.preferredLanguage,
      consultationType: form.values.consultationType!,
      preferredDate: form.values.scheduleDate
        ? format(form.values.scheduleDate, 'yyyy-MM-dd')
        : undefined,
      preferredTimeSlot: form.values.scheduleTime!,
      businessNeedsCategory: form.values.businessNeedsCategory!,
      commercialRetailOfficeOptions:
        form.values.businessNeedsCategory ===
        BusinessNeedsCategory.CommercialRetailOffice
          ? form.values.commercialRetailOfficeOptions
          : undefined,
      largeCommercialIndustrialOptions:
        form.values.businessNeedsCategory ===
        BusinessNeedsCategory.LargeCommercialIndustrial
          ? form.values.largeCommercialIndustrialOptions
          : undefined,
      otherBusinessNeed: form.values.otherBusinessNeed,
      comments: form.values.comments,
    };

    try {
      setIsFormSubmitting(true);
      setPublicFormErrorMessage('');
      const { data } = await createPublicEnergyConsultationRequest({
        variables: {
          payload,
        },
      });
      if (data?.submitEnergyConsultationWat?.success) {
        form.reset({ ...EnergyConsultationFormDefaultValues });
        setFormKey(prev => prev + 1);
        onSuccess?.();
      } else {
        setPublicFormErrorMessage(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'));
      }
    } catch (e) {
      const graphQLError = (e as ApolloError)?.graphQLErrors?.[0];
      const errorCode =
        graphQLError?.extensions?.code ||
        (graphQLError as typeof graphQLError & { code?: string })?.code;

      if (errorCode === 'NOT_COMMERCIAL_ACCOUNT') {
        setPublicFormErrorMessage(
          t('ENERGY_CONSULTATION_NOT_COMMERCIAL_ACCOUNT'),
        );
        return;
      }

      setPublicFormErrorMessage(t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'));
    } finally {
      setIsFormSubmitting(false);
    }
  });

  return {
    formKey,
    accounts: accountsData?.getAccountDetailList?.accounts,
    loading: loadingAccounts || loadingCustomer,
    form,
    //Enable calendar days based on timeslot availability
    availableSiteVisitDays:
      availableDaysData?.getEnergyConsultationMonthAvailability?.days || [],
    isFormSubmitting,
    publicFormErrorMessage,
    onSubmit,
    onSubmitPublicForm,
  };
};

export default useEnergyConsultationForm;
